<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import {
  LayoutDashboard, Map as MapIcon, BarChart3, Sparkles, Inbox, Users, Palette, LifeBuoy, Settings,
  ShieldCheck, LogOut, PanelLeft, Plus, ChevronDown, Globe,
} from 'lucide-vue-next'
import BrandLogo from '@/components/brand/BrandLogo.vue'
import api from '@/lib/api'
import { availableLanguages, setLocale, getLocale, type SupportedLocale } from '@/i18n'
import { usePlan } from '@/composables/usePlan'
import NotificationsBell from '@/components/layout/NotificationsBell.vue'
import PastDueBanner from '@/components/billing/PastDueBanner.vue'
import { billingLink, trackUpgradeClick } from '@/lib/upsell'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t } = useI18n()

// Plan + credits
const {
  isFree,
  isPaid,
  creditsRemaining,
  planName,
  planInfo,
  tourLimitReached,
  storageLimitReached,
  fetchPlan,
  loading: planLoading,
} = usePlan()
const showCreditsPopover = ref(false)
const showPlanPopover = ref(false)
const creditsPopoverRef = ref<HTMLElement | null>(null)
const planPopoverRef = ref<HTMLElement | null>(null)

// Only show billing CTAs to org admins/owners (type <= 2)
// Show billing CTA only to users who have an org (can subscribe) and are not internal staff
const canManageBilling = computed(() => {
  const u = authStore.user
  if (!u) return false
  const domain = u.email?.split('@')[1]?.toLowerCase()
  const isInternalStaff = domain === 'virtualtoureasy.com' || domain === 'remarso.org'
  return !!u.organization && !isInternalStaff
})

// Credit bar fill %
const creditPct = computed(() => {
  const info = planInfo.value
  const total = (info?.ai_credits ?? 0) + (info?.bonus_credits ?? 0)
  if (!info || total === 0) return 0
  return Math.min(100, Math.round((info.credits_remaining / total) * 100))
})


function toggleCreditsPopover(e: Event) {
  e.stopPropagation()
  showCreditsPopover.value = !showCreditsPopover.value
  showPlanPopover.value = false
  if (showCreditsPopover.value) loadCosts()
}

// Live credit prices (config-driven on the API) for the popover, loaded on first open.
const costs = ref<{ scene?: number; enhance?: number; floorplan?: number; videoPerSecond?: number } | null>(null)
async function loadCosts() {
  if (costs.value) return
  try {
    const data = await api.getAiUsage<{ costs?: Record<string, number>; video_pricing?: { tiers?: Array<{ credits_per_second?: Record<string, number> }> } }>()
    const videoRates = (data.video_pricing?.tiers || []).flatMap(tier => Object.values(tier.credits_per_second || {})).filter(n => n > 0)
    costs.value = {
      scene: data.costs?.['world-generate'] ?? data.costs?.['generate-360'],
      enhance: data.costs?.enhance,
      floorplan: data.costs?.['generate-floorplan'],
      videoPerSecond: videoRates.length ? Math.min(...videoRates) : undefined,
    }
  } catch { costs.value = {} }
}

function togglePlanPopover(e: Event) {
  e.stopPropagation()
  showPlanPopover.value = !showPlanPopover.value
  showCreditsPopover.value = false
}

function onDocClickCredits(e: MouseEvent) {
  if (creditsPopoverRef.value && !creditsPopoverRef.value.contains(e.target as Node)) {
    showCreditsPopover.value = false
  }
  if (planPopoverRef.value && !planPopoverRef.value.contains(e.target as Node)) {
    showPlanPopover.value = false
  }
}

const creditTotal = computed(() => {
  const info = planInfo.value
  return (info?.ai_credits ?? 0) + (info?.bonus_credits ?? 0)
})

const planLimitReached = computed(() =>
  tourLimitReached.value || storageLimitReached.value || creditsRemaining.value === 0
)
/** Renewal failed: never present the org as a plain Free account while an invoice is open. */
const isPastDue = computed(() => planInfo.value?.status === 'past_due')

// Upsell links: one deep link + `upgrade_cta_clicked {source}` each.
const upgradeLink = (source: string) => billingLink({ source })
const creditsLink = (source: string) => billingLink({ source, focus: 'credits' })

const tourUsagePct = computed(() => {
  const info = planInfo.value
  if (!info || info.max_tours >= 999999) return 0
  return Math.min(100, Math.round((info.current_tours / info.max_tours) * 100))
})

const storageUsagePct = computed(() => {
  const info = planInfo.value
  if (!info || info.max_storage_bytes <= 0 || info.max_storage_bytes >= 999999 * 1024 * 1024) return 0
  return Math.min(100, Math.round((info.storage_used_bytes / info.max_storage_bytes) * 100))
})

function formatBytes(bytes: number | undefined | null): string {
  const value = Number(bytes || 0)
  if (value >= 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`
  if (value >= 1024 * 1024) return `${Math.round((value / 1024 / 1024) * 10) / 10} MB`
  if (value >= 1024) return `${Math.round(value / 1024)} KB`
  return `${value} B`
}

function formatLimitMb(mb: number | undefined | null): string {
  const value = Number(mb || 0)
  if (value >= 1024) return `${Math.round((value / 1024) * 10) / 10} GB`
  return `${Math.round(value)} MB`
}

onMounted(async () => {
  await fetchPlan()
  document.addEventListener('click', onDocClickCredits)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClickCredits)
})

router.afterEach(() => {
  if (isFree.value || isPaid.value) fetchPlan(true) // refresh on navigation
})

// On mobile, sidebar starts closed. On desktop, starts open.
const isMobile = ref(window.innerWidth < 768)
const sidebarOpen = ref(!isMobile.value)

// Update on resize
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const wasMobile = isMobile.value
    isMobile.value = window.innerWidth < 768
    // Auto-close on shrink to mobile, auto-open on expand to desktop
    if (!wasMobile && isMobile.value) sidebarOpen.value = false
    if (wasMobile && !isMobile.value) sidebarOpen.value = true
  })
}

const navigation = computed(() => {
  const items = [
    { name: t('nav.dashboard'), path: '/dashboard', icon: LayoutDashboard },
    { name: t('nav.tours'), path: '/tours', icon: MapIcon },
    { name: t('shell.analytics'), path: '/analytics', icon: BarChart3 },
    { name: t('shell.aiUsage'), path: '/usage', icon: Sparkles },
    { name: t('nav.leads'), path: '/leads', icon: Inbox },
    // Team is visible to all - page handles plan gating
    { name: t('shell.team'), path: '/team', icon: Users },
  ]
  // Branding only for admins (Owner/Admin of org)
  if (authStore.isAdmin) {
    items.push({ name: t('shell.branding'), path: '/branding', icon: Palette })
  }
  items.push({ name: t('nav.support'), path: '/support', icon: LifeBuoy })
  items.push({ name: t('nav.settings'), path: '/settings', icon: Settings })
  return items
})

const initials = computed(() => (authStore.fullName || authStore.user?.firstName || authStore.user?.email || 'U').trim().charAt(0).toUpperCase())

const isActive = (path: string) => {
  return route.path === path || route.path.startsWith(path + '/')
}

// Close sidebar on navigation (mobile)
router.afterEach(() => {
  if (isMobile.value) sidebarOpen.value = false
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

// Language selector
const langOpen = ref(false)
const currentLang = ref(getLocale())

function switchLang(code: string) {
  setLocale(code as SupportedLocale)
  currentLang.value = code as SupportedLocale
  langOpen.value = false
}
</script>


<template>
  <div class="min-h-screen bg-background">
    <!-- Mobile backdrop -->
    <div
      v-if="isMobile && sidebarOpen"
      class="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-[2px] transition-opacity"
      @click="sidebarOpen = false"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-40 flex flex-col border-r bg-white transition-all duration-300',
        isMobile
          ? (sidebarOpen ? 'w-64 translate-x-0 shadow-2xl' : 'w-64 -translate-x-full')
          : (sidebarOpen ? 'w-64' : 'w-[68px]')
      ]"
    >
      <!-- Logo -->
      <div class="flex h-16 shrink-0 items-center px-5" :class="{ 'justify-center px-0': !sidebarOpen }">
        <router-link to="/dashboard" class="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="VirtualTourEasy">
          <BrandLogo :size="28" :wordmark="sidebarOpen" />
        </router-link>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        <router-link
          v-for="item in navigation"
          :key="item.path"
          :to="item.path"
          :title="sidebarOpen ? undefined : item.name"
          :class="[
            'group relative flex h-9 items-center gap-3 rounded-[10px] px-3 text-[13.5px] font-medium transition-colors',
            !sidebarOpen && 'justify-center px-0',
            isActive(item.path)
              ? 'bg-slate-100 text-slate-900'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          ]"
        >
          <span v-if="isActive(item.path)" class="absolute -left-3 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-gradient-to-b from-blue-500 to-violet-500" />
          <component :is="item.icon" class="h-[18px] w-[18px] shrink-0" :class="isActive(item.path) ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'" :stroke-width="1.9" />
          <span v-if="sidebarOpen" class="truncate">{{ item.name }}</span>
        </router-link>

        <!-- Admin Link (only for admins) -->
        <template v-if="authStore.isAdmin">
          <div class="mx-1 my-3 border-t" />
          <router-link
            to="/admin"
            :title="sidebarOpen ? undefined : t('nav.admin')"
            :class="[
              'flex h-9 items-center gap-3 rounded-[10px] px-3 text-[13.5px] font-medium transition-colors',
              !sidebarOpen && 'justify-center px-0',
              isActive('/admin') ? 'bg-orange-50 text-orange-700' : 'text-orange-600 hover:bg-orange-50'
            ]"
          >
            <ShieldCheck class="h-[18px] w-[18px] shrink-0" :stroke-width="1.9" />
            <span v-if="sidebarOpen">{{ t('nav.admin') }}</span>
          </router-link>
        </template>
      </nav>

      <!-- Plan card -->
      <div v-if="sidebarOpen && !planLoading && planInfo" class="px-3 pb-3">
        <router-link v-if="isPastDue && canManageBilling" to="/billing" class="block rounded-2xl border border-red-200 bg-red-50 p-4 transition-colors hover:bg-red-100/60">
          <div class="flex items-center justify-between text-xs font-semibold">
            <span class="text-red-900">{{ t('shell.planNamed', { name: planName }) }}</span>
            <span class="rounded-full bg-red-100 px-2 py-0.5 text-[11px] text-red-700">{{ t('shell.paymentDue') }}</span>
          </div>
          <p class="mt-2 text-[12.5px] leading-snug text-red-800">{{ t('shell.paymentDueLine') }}</p>
        </router-link>
        <div v-else-if="isFree && canManageBilling" class="ring-brand rounded-2xl p-4 shadow-soft">
          <div class="flex items-center justify-between text-xs font-semibold">
            <span class="text-slate-900">{{ t('shell.freePlan') }}</span>
            <span class="tabular-nums text-muted-foreground">{{ planInfo.current_tours }}/{{ planInfo.max_tours }} {{ t('shell.toursShort') }}</span>
          </div>
          <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div class="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all" :style="{ width: `${Math.max(tourUsagePct, 4)}%` }" />
          </div>
          <p class="mt-3 text-[12.5px] leading-snug text-slate-600">{{ t('shell.unlockLine') }}</p>
          <router-link :to="upgradeLink('sidebar_plan_card')" class="btn-brand mt-3 h-9 w-full text-[13px]" @click="trackUpgradeClick('sidebar_plan_card')">
            <Sparkles class="h-3.5 w-3.5" />{{ t('shell.seePlans') }}
          </router-link>
        </div>
        <router-link v-else to="/billing" class="block rounded-2xl border bg-slate-50/60 p-4 transition-colors hover:bg-slate-50">
          <div class="flex items-center justify-between text-xs font-semibold">
            <span class="text-slate-900">{{ t('shell.planNamed', { name: planName }) }}</span>
            <span class="tabular-nums text-muted-foreground">{{ creditsRemaining.toLocaleString() }} {{ t('credits.label') }}</span>
          </div>
          <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200/70">
            <div class="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all" :style="{ width: `${creditPct}%` }" />
          </div>
        </router-link>
      </div>

      <!-- User -->
      <div class="flex shrink-0 items-center gap-3 border-t px-4 py-3" :class="{ 'justify-center px-0': !sidebarOpen }">
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-[13px] font-semibold text-white" :title="authStore.user?.email">{{ initials }}</span>
        <div v-if="sidebarOpen" class="min-w-0 flex-1">
          <p class="truncate text-[13px] font-semibold text-slate-900">{{ authStore.fullName || t('common.user') }}</p>
          <p class="truncate text-xs text-muted-foreground">{{ authStore.user?.email }}</p>
        </div>
        <button
          v-if="sidebarOpen"
          type="button"
          class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          :title="t('auth.logout')"
          :aria-label="t('auth.logout')"
          @click="handleLogout"
        >
          <LogOut class="h-4 w-4" />
        </button>
      </div>
    </aside>

    <!-- Main content -->
    <div :class="['transition-all duration-300', isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-[68px]')]">
      <!-- Top bar -->
      <header class="sticky top-0 z-20 flex h-16 items-center gap-2 border-b bg-white/80 px-4 backdrop-blur-xl sm:gap-3 sm:px-6">
        <button
          type="button"
          class="-ml-1 rounded-[10px] p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          :aria-label="t('shell.toggleSidebar')"
          @click="toggleSidebar"
        >
          <PanelLeft class="h-5 w-5" :stroke-width="1.9" />
        </button>

        <div class="flex-1"></div>

        <!-- Upgrade (free plan, billing admins only) -->
        <router-link
          v-if="canManageBilling && !planLoading && isFree && !isPastDue"
          :to="upgradeLink('topbar_upgrade')"
          class="ring-brand hidden h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-slate-800 transition-shadow hover:shadow-soft md:inline-flex"
          @click="trackUpgradeClick('topbar_upgrade')"
        >
          <Sparkles class="h-4 w-4 text-violet-500" />{{ t('shell.upgradeCta') }}
        </router-link>

        <!-- Plan chip -->
        <div v-if="!planLoading" class="relative" ref="planPopoverRef">
          <button
            type="button"
            class="hidden h-9 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] transition-colors hover:bg-slate-50 sm:inline-flex"
            :class="planLimitReached || isPastDue ? 'border-red-200 text-red-600' : 'text-slate-700'"
            :title="t('shell.planLimits')"
            @click="togglePlanPopover"
          >
            <span class="font-semibold">{{ planName }}</span>
            <span v-if="isPastDue" class="text-xs font-semibold">· {{ t('shell.paymentDue') }}</span>
            <span v-if="planLimitReached || isPastDue" class="h-1.5 w-1.5 rounded-full bg-red-500"></span>
            <ChevronDown class="h-3.5 w-3.5 text-slate-400" />
          </button>

          <div v-if="showPlanPopover" class="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border bg-white shadow-lift">
            <div class="flex items-center justify-between border-b px-4 py-3">
              <div class="text-sm font-semibold">{{ t('shell.planNamed', { name: planName }) }}</div>
              <span class="rounded-full px-2 py-0.5 text-[11px] font-semibold" :class="planLimitReached ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'">
                {{ planLimitReached ? t('shell.limitReached') : t('shell.withinLimits') }}
              </span>
            </div>
            <div class="space-y-4 p-4">
              <div class="space-y-1.5">
                <div class="flex justify-between text-[13px]">
                  <span class="text-muted-foreground">{{ t('shell.tours') }}</span>
                  <span class="font-semibold tabular-nums">{{ (planInfo?.current_tours ?? 0).toLocaleString() }}<span class="font-normal text-muted-foreground"> / {{ planInfo?.max_tours === 999999 ? '∞' : (planInfo?.max_tours ?? 0).toLocaleString() }}</span></span>
                </div>
                <div v-if="planInfo?.max_tours !== 999999" class="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div class="h-full rounded-full transition-all" :class="tourLimitReached ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-violet-500'" :style="`width: ${tourUsagePct}%`" />
                </div>
              </div>
              <div class="space-y-1.5">
                <div class="flex justify-between text-[13px]">
                  <span class="text-muted-foreground">{{ t('shell.storage') }}</span>
                  <span class="font-semibold tabular-nums">{{ formatBytes(planInfo?.storage_used_bytes) }}<span class="font-normal text-muted-foreground"> / {{ planInfo?.max_storage_mb === 999999 ? '∞' : formatLimitMb(planInfo?.max_storage_mb) }}</span></span>
                </div>
                <div v-if="planInfo?.max_storage_mb !== 999999" class="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div class="h-full rounded-full transition-all" :class="storageLimitReached ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-violet-500'" :style="`width: ${storageUsagePct}%`" />
                </div>
                <div class="text-xs text-muted-foreground">{{ t('shell.filesStored', { n: (planInfo?.storage_file_count ?? 0).toLocaleString() }) }}</div>
              </div>
              <div class="space-y-1.5">
                <div class="flex justify-between text-[13px]">
                  <span class="text-muted-foreground">{{ t('credits.aiCredits') }}</span>
                  <span class="font-semibold tabular-nums">{{ creditsRemaining.toLocaleString() }}<span class="font-normal text-muted-foreground"> / {{ creditTotal.toLocaleString() }}</span></span>
                </div>
                <div class="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div class="h-full rounded-full transition-all" :class="creditsRemaining === 0 ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-violet-500'" :style="`width: ${creditPct}%`" />
                </div>
              </div>
              <div v-if="planLimitReached" class="rounded-[10px] bg-red-50 px-3 py-2 text-xs text-red-700">{{ t('shell.limitHelp') }}</div>
              <router-link :to="upgradeLink('plan_popover')" class="btn-brand h-9 w-full" @click="showPlanPopover = false; trackUpgradeClick('plan_popover')">{{ isFree ? t('shell.seePlans') : t('credits.upgradeCta') }}</router-link>
            </div>
          </div>
        </div>

        <NotificationsBell />

        <!-- AI credits chip -->
        <div v-if="!planLoading" class="relative" ref="creditsPopoverRef">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] transition-colors hover:bg-slate-50"
            :class="creditsRemaining === 0 ? 'border-red-200 text-red-600' : 'text-slate-700'"
            :title="t('credits.aiCredits')"
            @click="toggleCreditsPopover"
          >
            <Sparkles class="h-4 w-4" :class="creditsRemaining === 0 ? 'text-red-500' : 'text-violet-500'" />
            <span class="font-semibold tabular-nums">{{ creditsRemaining.toLocaleString() }}</span>
            <span class="hidden text-xs text-muted-foreground sm:inline">{{ t('credits.label') }}</span>
          </button>

          <div v-if="showCreditsPopover" class="absolute right-0 top-full z-50 mt-2 w-[19rem] overflow-hidden rounded-2xl border bg-white shadow-lift">
            <div class="relative overflow-hidden bg-[#0b1426] px-4 pb-4 pt-3.5 text-white">
              <div class="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-violet-500/30 blur-3xl" />
              <div class="relative flex items-center justify-between text-xs font-semibold text-white/70">
                <span class="inline-flex items-center gap-1.5"><Sparkles class="h-3.5 w-3.5 text-violet-300" />{{ t('credits.aiCredits') }}</span>
                <span>{{ t('shell.planNamed', { name: planName }) }}</span>
              </div>
              <div class="relative mt-2 flex items-baseline gap-1.5">
                <span class="text-3xl font-bold tabular-nums tracking-tight">{{ creditsRemaining.toLocaleString() }}</span>
                <span class="text-sm text-white/60">/ {{ creditTotal.toLocaleString() }}</span>
              </div>
              <div class="relative mt-3 h-1.5 overflow-hidden rounded-full bg-white/15">
                <div class="h-full rounded-full bg-gradient-to-r from-blue-400 via-violet-400 to-pink-400 transition-all" :style="`width: ${creditPct}%`" />
              </div>
              <div class="relative mt-2 flex justify-between text-[11px] text-white/60">
                <span>{{ t('credits.used', { n: (planInfo?.credits_used_this_month ?? 0).toLocaleString() }) }}</span>
                <span v-if="(planInfo?.bonus_credits ?? 0) > 0" class="text-amber-300">{{ t('credits.bonus', { n: planInfo?.bonus_credits.toLocaleString() }) }}</span>
              </div>
            </div>

            <div class="space-y-3 p-4">
              <div class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{{ t('shell.whatCreditsBuy') }}</div>
              <div v-if="!costs" class="space-y-2"><div v-for="n in 3" :key="n" class="skeleton h-4 rounded" /></div>
              <ul v-else class="space-y-2 text-[13px]">
                <li v-if="costs.scene" class="flex justify-between"><span class="text-slate-600">{{ t('shell.costScene') }}</span><span class="font-semibold tabular-nums">{{ costs.scene }}</span></li>
                <li v-if="costs.enhance" class="flex justify-between"><span class="text-slate-600">{{ t('shell.costEnhance') }}</span><span class="font-semibold tabular-nums">{{ costs.enhance }}</span></li>
                <li v-if="costs.floorplan" class="flex justify-between"><span class="text-slate-600">{{ t('shell.costFloorplan') }}</span><span class="font-semibold tabular-nums">{{ costs.floorplan }}</span></li>
                <li v-if="costs.videoPerSecond" class="flex justify-between"><span class="text-slate-600">{{ t('shell.costVideo') }}</span><span class="font-semibold tabular-nums">{{ t('shell.perSecond', { n: costs.videoPerSecond }) }}</span></li>
              </ul>
              <div class="flex gap-2 pt-1">
                <router-link to="/usage" class="flex h-9 flex-1 items-center justify-center rounded-[10px] border text-[13px] font-medium transition-colors hover:bg-slate-50" @click="showCreditsPopover = false">{{ t('credits.viewUsage') }}</router-link>
                <router-link :to="creditsLink('credits_popover')" class="btn-brand h-9 flex-1 text-[13px]" @click="showCreditsPopover = false; trackUpgradeClick('credits_popover', { feature: 'credits' })">{{ isFree ? t('shell.getCredits') : t('credits.topUp') }}</router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Language Selector -->
        <div class="relative">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-1 rounded-[10px] px-2 text-sm transition-colors hover:bg-slate-100"
            :aria-label="t('shell.language')"
            @click="langOpen = !langOpen"
          >
            <Globe class="h-4 w-4 text-slate-500" />
            <span class="text-xs font-semibold uppercase text-slate-600">{{ currentLang }}</span>
          </button>
          <div v-if="langOpen" class="absolute right-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-xl border bg-white py-1 shadow-lift">
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              type="button"
              :class="[
                'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50',
                lang.code === currentLang ? 'font-semibold text-slate-900' : 'text-slate-600'
              ]"
              @click="switchLang(lang.code)"
            >
              <span class="w-6 text-[11px] font-bold uppercase text-slate-400">{{ lang.code }}</span>
              <span>{{ lang.nativeName }}</span>
            </button>
          </div>
        </div>

        <!-- Quick action -->
        <router-link to="/tours?create=menu" class="btn-brand h-9 px-3 sm:px-4">
          <Plus class="h-4 w-4" :stroke-width="2.4" />
          <span class="hidden sm:inline">{{ t('nav.newTour') }}</span>
        </router-link>
      </header>

      <PastDueBanner :can-manage-billing="canManageBilling" />

      <!-- Page content -->
      <main>
        <slot />
      </main>
    </div>
  </div>
</template>
